import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Users, LogIn, Clock, LogOut, CalendarDays, UserX } from 'lucide-react';

const RADIUS = 48;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function GaugeRing({ color, progress, glowing }) {
    const offset = CIRCUMFERENCE * (1 - Math.max(0, Math.min(1, progress)));
    return (
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle
                cx="60"
                cy="60"
                r={RADIUS}
                fill="none"
                stroke="rgba(100, 116, 139, 0.22)"
                strokeWidth="10"
            />
            <circle
                cx="60"
                cy="60"
                r={RADIUS}
                fill="none"
                stroke={color}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
                style={{
                    filter: glowing ? `drop-shadow(0 0 8px ${color})` : 'none',
                    transition: 'filter 180ms linear'
                }}
            />
        </svg>
    );
}

function StatCard({ item, index, total, onCardClick }) {
    const target = total > 0 ? Math.min(1, item.value / total) : 0;
    const [progress, setProgress] = useState(0);
    const [glowing, setGlowing] = useState(false);
    const playedRef = useRef(false);

    useEffect(() => {
        const reduceMotion = typeof window !== 'undefined'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduceMotion) {
            setProgress(target);
            playedRef.current = true;
            return undefined;
        }

        if (playedRef.current) {
            setProgress(target);
            return undefined;
        }

        let frame = 0;
        const start = performance.now();
        const wait = index * 90;
        const sweepMs = 380;
        const holdMs = 90;
        const settleMs = 620;

        const tick = (now) => {
            const t = now - start - wait;
            if (t < 0) {
                frame = requestAnimationFrame(tick);
                return;
            }
            if (t <= sweepMs) {
                setGlowing(true);
                setProgress(easeOutQuart(t / sweepMs));
                frame = requestAnimationFrame(tick);
                return;
            }
            if (t <= sweepMs + holdMs) {
                setProgress(1);
                frame = requestAnimationFrame(tick);
                return;
            }
            if (t <= sweepMs + holdMs + settleMs) {
                const p = easeInOutCubic((t - sweepMs - holdMs) / settleMs);
                setProgress(1 - p * (1 - target));
                if (p > 0.65) setGlowing(false);
                frame = requestAnimationFrame(tick);
                return;
            }
            setProgress(target);
            setGlowing(false);
            playedRef.current = true;
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, index]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.45, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full bg-white dark:bg-gray-800 rounded-lg shadow-md border dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow duration-300"
            onClick={() => onCardClick(item.category, item.label, item.value)}
        >
            <div className="px-3 py-2.5 h-[5.5rem] flex items-center gap-3">
                <div className="relative w-[4.5rem] h-[4.5rem] shrink-0">
                    <GaugeRing color={item.color} progress={progress} glowing={glowing} />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <item.icon size={16} style={{ color: item.color }} />
                    </div>
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 leading-none tabular-nums">{item.value}</p>
                    <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-300 leading-tight truncate">{item.label}</p>
                </div>
            </div>
        </motion.div>
    );
}

export default function CircularStatsPanel({
    totalEmployees,
    insideEmployeesCount,
    outsideEmployeesCount,
    lateArrivalsCount,
    onLeaveCount,
    absentCount,
    onCardClick
}) {
    const [stats, setStats] = useState({
        toplam: totalEmployees || 0,
        icerde: insideEmployeesCount || 0,
        disarida: outsideEmployeesCount || 0,
        gecGelen: lateArrivalsCount || 0,
        izinli: onLeaveCount || 0,
        gelmeyen: absentCount || 0
    });

    useEffect(() => {
        setStats({
            toplam: totalEmployees || 0,
            icerde: insideEmployeesCount || 0,
            disarida: outsideEmployeesCount || 0,
            gecGelen: lateArrivalsCount || 0,
            izinli: onLeaveCount || 0,
            gelmeyen: absentCount || 0
        });
    }, [totalEmployees, insideEmployeesCount, outsideEmployeesCount, lateArrivalsCount, onLeaveCount, absentCount]);

    const items = [
        { label: "Toplam Personel", value: stats.toplam, color: "#34D399", icon: Users, category: "totalEmployees" },
        { label: "İçeride", value: stats.icerde, color: "#2563EB", icon: LogIn, category: "insideEmployees" },
        { label: "Dışarıda", value: stats.disarida, color: "#8B5CF6", icon: LogOut, category: "outsideEmployees" },
        { label: "Geç Gelen", value: stats.gecGelen, color: "#EF4444", icon: Clock, category: "lateArrivalsEmployees" },
        { label: "İzinli", value: stats.izinli, color: "#FBBF24", icon: CalendarDays, category: "onLeaveEmployees" },
        { label: "Gelmeyen", value: stats.gelmeyen, color: "#6B7280", icon: UserX, category: "absentEmployees" },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
            {items.map((item, index) => (
                <StatCard
                    key={item.category}
                    item={item}
                    index={index}
                    total={item.category === 'totalEmployees' ? item.value : stats.toplam}
                    onCardClick={onCardClick}
                />
            ))}
        </div>
    );
}
